import { useMemo, useState } from "react";
import { ArrowClockwise, Broadcast, Lightning, MapPin, Sparkle } from "@phosphor-icons/react";

import { StoryCard } from "@/components/StoryCard";
import { MajorSceneCard } from "@/components/MajorSceneCard";
import { VerticalVideoFrame } from "@/components/VerticalVideoFrame";
import { usePageSeo } from "@/hooks/usePageSeo";
import { Button } from "@/components/ui/button";
import { getStoryCategoryData } from "@/utils/storyCategorization";
import { getStorySection } from "@/utils/storySections";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const sourceOptions = [
  { value: "all", label: "All sources" },
  { value: "youtube", label: "YouTube" },
  { value: "manual", label: "Office uploads" },
];

const FALLBACK_STUDIO_IMAGE = "https://images.unsplash.com/photo-1742805382149-3c2f0cd0f300?auto=format&fit=crop&w=1600&q=80";

const BreakingHero = ({ siteData, featuredStory }) => (
  <section className="relative overflow-hidden border border-[rgba(10,15,26,0.12)]" data-testid="breaking-news-hero">
    <div className="absolute inset-0">
      <img alt="Control room" className="news-image h-full w-full" src={siteData?.studio_image || FALLBACK_STUDIO_IMAGE} />
      <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/88 to-slate-950/42" />
    </div>
    <div className="relative grid gap-8 px-6 py-10 lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-10 lg:px-10 lg:py-14">
      <div>
        <p className="text-base font-black uppercase tracking-[0.34em] text-red-500 sm:text-lg lg:text-[1.4rem]" data-testid="breaking-news-overline">R.I.P. MAINSTREAM MEDIA</p>
        <h1 className="headline-xl mt-4 text-white" data-testid="breaking-news-title">Breaking News</h1>
        <p className="copy-measure mt-5 text-base leading-8 text-slate-200 sm:text-lg" data-testid="breaking-news-description">
          Houston-first incident coverage, Texas field updates, and rapid-response video summaries — delivered as the story happens, not when other outlets decide to post it.
        </p>
        <p className="mt-4 max-w-2xl border-l-2 border-red-500 pl-4 text-sm font-semibold uppercase tracking-[0.18em] text-slate-100" data-testid="breaking-news-slogan">
          Your headquarters for news now!
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <div className="inline-flex items-center gap-2 border border-white/12 bg-white/8 px-4 py-3 text-xs font-bold uppercase tracking-[0.18em] text-white" data-testid="breaking-news-live-chip">
            <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" />
            Houston first
          </div>
          <div className="inline-flex items-center gap-2 border border-white/12 bg-white/8 px-4 py-3 text-xs font-bold uppercase tracking-[0.18em] text-white" data-testid="breaking-news-refresh-chip">
            <ArrowClockwise size={18} weight="bold" />
            {siteData?.sync_status?.source_mode === "youtube" ? "Live feed active" : "Newsroom feed active"}
          </div>
        </div>
      </div>

      <div className="space-y-4" data-testid="breaking-news-featured-video-stack">
        {featuredStory ? (
          <VerticalVideoFrame compact story={featuredStory} />
        ) : (
          <div className="border border-white/10 bg-white/5 p-5 text-white" data-testid="breaking-news-featured-empty-state">
            <p className="eyebrow text-red-400">Awaiting breaking update</p>
            <p className="mt-3 text-sm leading-7 text-slate-200">When the newsroom tags a story as breaking, it will appear here first.</p>
          </div>
        )}
      </div>
    </div>
  </section>
);

const BreakingToolbar = ({ onRefresh, onSourceChange, refreshing, selectedSource }) => (
  <div className="mb-8 flex flex-col gap-4 border border-[rgba(10,15,26,0.12)] bg-white p-5 shadow-[0_20px_60px_rgba(15,23,42,0.04)] sm:flex-row sm:items-center sm:justify-between" data-testid="breaking-news-toolbar">
    <div>
      <p className="eyebrow text-red-600" data-testid="breaking-news-toolbar-overline">Control filters</p>
      <h2 className="mt-2 font-['Outfit'] text-2xl font-bold tracking-tight text-slate-950" data-testid="breaking-news-toolbar-title">Filter the live desk and refresh the latest breaking coverage.</h2>
    </div>
    <div className="flex flex-col gap-3 sm:flex-row">
      <div className="min-w-[220px]" data-testid="breaking-news-source-filter">
        <Select onValueChange={onSourceChange} value={selectedSource}>
          <SelectTrigger className="h-12 rounded-none border-[rgba(10,15,26,0.12)] bg-white text-sm font-semibold" data-testid="source-select-trigger">
            <SelectValue placeholder="Select source" />
          </SelectTrigger>
          <SelectContent>
            {sourceOptions.map((option) => (
              <SelectItem key={option.value} value={option.value} data-testid={`source-select-item-${option.value}`}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Button className="rounded-none bg-slate-950 px-5 py-6 text-xs font-bold uppercase tracking-[0.2em] text-white hover:bg-slate-800" data-testid="refresh-newsroom-button" onClick={onRefresh}>
        <ArrowClockwise size={18} weight="bold" className={refreshing ? "animate-spin" : ""} />
        {refreshing ? "Refreshing" : "Refresh Now"}
      </Button>
    </div>
  </div>
);

const LiveVideoFeedPanel = ({ liveVideoStories }) => (
  <div className="border border-[rgba(10,15,26,0.12)] bg-white p-5" data-testid="live-video-feed">
    <p className="eyebrow text-red-600">Live video feed</p>
    <h3 className="mt-2 font-['Outfit'] text-2xl font-bold tracking-tight text-slate-950">Live desk updates</h3>
    <div className="mt-5 space-y-4">
      {liveVideoStories.map((story) => (
        <div key={story.id} className="border border-[rgba(10,15,26,0.08)] p-4" data-testid={`live-video-item-${story.slug}`}>
          <div className="mb-3 inline-flex items-center gap-2 bg-slate-950 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-white">
            <Broadcast size={14} weight="bold" />
            {story.content_type}
          </div>
          <h4 className="font-['Outfit'] text-lg font-bold tracking-tight text-slate-950">{story.headline}</h4>
          <p className="mt-2 text-sm leading-6 text-slate-700">{story.excerpt}</p>
        </div>
      ))}
    </div>
  </div>
);

const AiSidebarPanel = () => (
  <div className="border border-[rgba(0,47,167,0.18)] bg-[rgba(0,47,167,0.04)] p-5" data-testid="breaking-news-ai-panel">
    <p className="eyebrow text-[#1338BE]">General overview</p>
    <h3 className="mt-2 font-['Outfit'] text-2xl font-bold tracking-tight text-slate-950">General Overview of Situation</h3>
    <div className="mt-4 space-y-3 text-sm leading-7 text-slate-700">
      <p className="inline-flex items-center gap-2" data-testid="breaking-news-ai-bullet-1">
        <Sparkle size={18} weight="fill" className="text-[#1338BE]" />
        Each video description is turned into a fast, readable summary of what happened.
      </p>
      <p className="inline-flex items-center gap-2" data-testid="breaking-news-ai-bullet-2">
        <Lightning size={18} weight="fill" className="text-[#D21404]" />
        The top story automatically follows the newest breaking update every hour.
      </p>
    </div>
  </div>
);

const StationsPanel = ({ stations }) => (
  <div className="border border-[rgba(10,15,26,0.12)] bg-white p-5" data-testid="breaking-news-stations-panel">
    <p className="eyebrow text-red-600">Texas offices</p>
    <h3 className="mt-2 font-['Outfit'] text-2xl font-bold tracking-tight text-slate-950">Network cities</h3>
    <div className="mt-4 space-y-3">
      {stations.map((station) => (
        <div key={station.city} className="flex items-start gap-3 border border-[rgba(10,15,26,0.08)] p-4" data-testid={`station-sidebar-item-${station.city.toLowerCase()}`}>
          <MapPin size={18} weight="bold" className="mt-1 text-[#D21404]" />
          <div>
            <p className="font-semibold text-slate-950">{station.city}, {station.state}</p>
            <p className="mt-1 text-sm leading-6 text-slate-700">{station.focus}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const CategoryOverviewPanel = ({
  activeCoverageArea,
  activeSceneCategory,
  coverageAreas,
  onCoverageAreaToggle,
  onReset,
  onSceneCategoryToggle,
  sceneCategories,
}) => (
  <div className="mb-8 border border-[rgba(10,15,26,0.12)] bg-white p-5 shadow-[0_20px_60px_rgba(15,23,42,0.04)]" data-testid="breaking-category-overview-panel">
    <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
      <div>
        <p className="eyebrow text-red-600" data-testid="breaking-filter-overline">Live feed filters</p>
        <h2 className="mt-2 font-['Outfit'] text-2xl font-bold tracking-tight text-slate-950" data-testid="breaking-filter-title">Tap a category or coverage area to narrow the feed instantly.</h2>
      </div>
      <button className="border border-[rgba(10,15,26,0.12)] px-4 py-3 text-xs font-bold uppercase tracking-[0.18em] text-slate-800 transition-colors hover:bg-slate-100" data-testid="breaking-reset-filters-button" onClick={onReset} type="button">
        Reset filters
      </button>
    </div>
    <div className="grid gap-6 lg:grid-cols-2">
      <div>
        <p className="eyebrow text-red-600" data-testid="breaking-scene-categories-overline">Scene categories</p>
        <div className="mt-4 flex flex-wrap gap-2" data-testid="breaking-scene-categories-list">
          {sceneCategories.map((category) => (
            <button
              key={category}
              className={`px-3 py-2 text-[11px] font-bold uppercase tracking-[0.16em] transition-colors ${activeSceneCategory === category ? "border border-[#D21404] bg-[#D21404] text-white" : "border border-[rgba(210,20,4,0.18)] bg-[rgba(210,20,4,0.06)] text-[#D21404]"}`}
              data-testid={`breaking-scene-category-${category.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => onSceneCategoryToggle(category)}
              type="button"
            >
              {category}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="eyebrow text-red-600" data-testid="breaking-coverage-areas-overline">Coverage areas</p>
        <div className="mt-4 flex flex-wrap gap-2" data-testid="breaking-coverage-areas-list">
          {coverageAreas.map((area) => (
            <button
              key={area}
              className={`px-3 py-2 text-[11px] font-bold uppercase tracking-[0.16em] transition-colors ${activeCoverageArea === area ? "border border-slate-950 bg-slate-950 text-white" : "border border-[rgba(10,15,26,0.12)] text-slate-700"}`}
              data-testid={`breaking-coverage-area-${area.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => onCoverageAreaToggle(area)}
              type="button"
            >
              {area}
            </button>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const IncidentTrackerStrip = ({ items, onActivate }) => (
  <section className="mb-8 border border-[rgba(10,15,26,0.12)] bg-slate-950 px-5 py-5 text-white shadow-[0_20px_60px_rgba(15,23,42,0.08)]" data-testid="breaking-incident-tracker-strip">
    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
      <div>
        <p className="eyebrow text-red-400" data-testid="breaking-incident-tracker-overline">Houston incident tracker</p>
        <h2 className="mt-2 font-['Outfit'] text-2xl font-bold tracking-tight text-white" data-testid="breaking-incident-tracker-title">Live clusters showing what the newsroom is covering right now.</h2>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="breaking-incident-tracker-items">
        {items.map((item) => (
          <button
            key={`${item.sceneCategory}-${item.coverageArea}`}
            className="border border-white/12 bg-white/6 px-4 py-3 text-left transition-colors hover:bg-white hover:text-slate-950"
            data-testid={`breaking-incident-tracker-item-${item.sceneCategory.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${item.coverageArea.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            onClick={() => onActivate(item)}
            type="button"
          >
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-red-300">{item.coverageArea}</p>
            <p className="mt-2 font-['Outfit'] text-lg font-bold tracking-tight">{item.sceneCategory}</p>
            <p className="mt-2 text-xs uppercase tracking-[0.16em] text-slate-300">{item.count} active stories</p>
          </button>
        ))}
      </div>
    </div>
  </section>
);

export const BreakingNewsPage = ({
  siteData,
  feed,
  selectedSource,
  onSourceChange,
  onRefresh,
  refreshing,
}) => {
  usePageSeo({
    canonicalPath: "/breaking-news",
    description: "Follow Houston breaking news, live incidents, police activity, crashes, storm coverage, and rapid-response video updates from Content Watch HQ.",
    title: "Breaking News | Content Watch HQ",
  });

  const [activeSceneCategory, setActiveSceneCategory] = useState(null);
  const [activeCoverageArea, setActiveCoverageArea] = useState(null);
  const breakingStories = useMemo(
    () => feed.filter((story) => getStorySection(story) === "breaking"),
    [feed],
  );
  const sceneCategories = useMemo(
    () => [...new Set(breakingStories.map((story) => getStoryCategoryData(story).sceneCategory))],
    [breakingStories],
  );
  const coverageAreas = useMemo(
    () => [...new Set(breakingStories.map((story) => getStoryCategoryData(story).coverageArea))],
    [breakingStories],
  );
  const incidentTrackerItems = useMemo(() => {
    const trackerMap = new Map();
    breakingStories.forEach((story) => {
      const { coverageArea, sceneCategory } = getStoryCategoryData(story);
      const key = `${sceneCategory}-${coverageArea}`;
      const currentItem = trackerMap.get(key);
      if (currentItem) {
        currentItem.count += 1;
        return;
      }
      trackerMap.set(key, { sceneCategory, coverageArea, count: 1 });
    });
    return [...trackerMap.values()].sort((left, right) => right.count - left.count).slice(0, 4);
  }, [breakingStories]);
  const filteredFeed = useMemo(
    () => breakingStories.filter((story) => {
      const { coverageArea, sceneCategory } = getStoryCategoryData(story);
      const matchesScene = activeSceneCategory ? sceneCategory === activeSceneCategory : true;
      const matchesArea = activeCoverageArea ? coverageArea === activeCoverageArea : true;
      return matchesScene && matchesArea;
    }),
    [activeCoverageArea, activeSceneCategory, breakingStories],
  );
  const featuredStory = filteredFeed?.[0] || breakingStories?.[0] || null;
  const secondaryStories = filteredFeed?.slice(1, 5) || [];
  const liveVideoStories = filteredFeed?.filter((story) => ["video", "short"].includes(story.content_type)).slice(0, 4) || [];

  const toggleSceneCategory = (category) => {
    setActiveSceneCategory((current) => (current === category ? null : category));
  };

  const toggleCoverageArea = (area) => {
    setActiveCoverageArea((current) => (current === area ? null : area));
  };

  const resetFilters = () => {
    setActiveSceneCategory(null);
    setActiveCoverageArea(null);
  };

  return (
    <main className="page-shell mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-14 lg:px-8 lg:py-16" data-testid="breaking-news-page">
      <BreakingHero featuredStory={featuredStory} siteData={siteData} />

      <section className="mt-10 grid gap-8 xl:grid-cols-[minmax(0,1fr)_320px]" data-testid="breaking-news-content-grid">
        <div>
          <IncidentTrackerStrip
            items={incidentTrackerItems}
            onActivate={(item) => {
              setActiveSceneCategory(item.sceneCategory);
              setActiveCoverageArea(item.coverageArea);
            }}
          />
          <CategoryOverviewPanel
            activeCoverageArea={activeCoverageArea}
            activeSceneCategory={activeSceneCategory}
            coverageAreas={coverageAreas}
            onCoverageAreaToggle={toggleCoverageArea}
            onReset={resetFilters}
            onSceneCategoryToggle={toggleSceneCategory}
            sceneCategories={sceneCategories}
          />

          <div className="mb-8 border border-[rgba(10,15,26,0.12)] bg-white p-5 shadow-[0_20px_60px_rgba(15,23,42,0.04)]" data-testid="breaking-major-scenes-panel">
            <p className="eyebrow text-red-600" data-testid="breaking-major-scenes-overline">Pinned major scenes</p>
            <h2 className="mt-2 font-['Outfit'] text-2xl font-bold tracking-tight text-slate-950" data-testid="breaking-major-scenes-title">Most urgent uploads pinned for instant access.</h2>
            <div className="mt-5 grid grid-cols-1 gap-4 2xl:grid-cols-2">
              {breakingStories?.slice(0, 4).map((story, index) => (
                <MajorSceneCard key={story.id} rank={index + 1} story={story} />
              ))}
            </div>
          </div>

          <BreakingToolbar onRefresh={onRefresh} onSourceChange={onSourceChange} refreshing={refreshing} selectedSource={selectedSource} />

          <div className="grid grid-cols-1 gap-6 md:grid-cols-3 lg:grid-cols-4" data-testid="breaking-news-bento-grid">
            {featuredStory ? (
              <div className="md:col-span-3 lg:col-span-2 lg:row-span-2" data-testid="breaking-news-featured-story">
                <StoryCard featured story={featuredStory} />
              </div>
            ) : null}
            {secondaryStories.map((story) => (
              <div key={story.id} className="md:col-span-1 lg:col-span-1" data-testid={`breaking-news-secondary-story-${story.slug}`}>
                <StoryCard story={story} />
              </div>
            ))}
          </div>
          {!filteredFeed.length ? (
            <div className="mt-6 border border-[rgba(10,15,26,0.12)] bg-white p-6" data-testid="breaking-news-empty-filter-state">
              <p className="eyebrow text-red-600">No matching stories</p>
              <p className="mt-3 text-sm leading-7 text-slate-700">Try another category or reset the filters to bring all current uploads back into view.</p>
            </div>
          ) : null}
        </div>

        <aside className="space-y-6" data-testid="breaking-news-sidebar">
          <LiveVideoFeedPanel liveVideoStories={liveVideoStories} />
          <AiSidebarPanel />
          <StationsPanel stations={siteData?.affiliate_stations || []} />
        </aside>
      </section>
    </main>
  );
};